/**
 * D3 · কার্ট — `/cart`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame D3 (360 × 680).
 *
 * Frame note: "The footer says পণ্যের দাম, not মোট — delivery and fees are not in this
 * number yet, and calling it a total here would be the first broken promise."
 *
 * ── WIRED ────────────────────────────────────────────────────────────────────
 * `GET /v1/cart`, and every stepper tap is `PATCH /v1/cart/items/:id` (or `DELETE`, when
 * it would take the line to zero) whose response IS the cart. The quantity on screen is
 * therefore the quantity the server holds, not a `useState` that could disagree with the
 * footer — and the footer's figure is the server's, which is how the cart and the
 * checkout quote are guaranteed to be about the same cart.
 *
 * `সঙ্গে লাগতে পারে` is the ADD door: a suggestion's `id` is the offering slug (the API
 * builds it as `id: s.slug`, and `POST /v1/cart/items` takes that same slug as
 * `offering_id`), so a tap is one `addItem` and the cart that comes back is what the
 * screen then shows. No `Idempotency-Key` is sent: `idempotent()` wraps exactly one route
 * in `apps/api/src/commerce/index.ts` — `POST /v1/orders` — and sending a key to a route
 * that does not read one is a claim about a guarantee this route has not made.
 *
 * ── WHY EVERY WRITE GOES THROUGH ONE QUEUE ───────────────────────────────────
 * `createWriteQueue` (`./writes.ts`) chains each mutation onto the one before it. Firing
 * them in parallel —
 * which is what a bare `void write.then(setCart)` per tap does — has two failure modes and
 * both end with the footer disagreeing with the server. Two quick taps on `+` send
 * `PATCH 3` and `PATCH 4`; if the responses land out of order the screen keeps the body of
 * the FIRST one and settles on ৩ and its total while the server holds 4, and nothing ever
 * corrects it because the screen only re-reads on mount. If the REQUESTS overtake each
 * other the server itself settles on 3 while the screen shows 4. A queue removes both: one
 * write is in flight at a time, so arrival order is issue order and the last response is
 * the last intent. That is what "the cart is server state" has to mean on a stepper.
 *
 * KNOWN DEFECT 1 (three instances on this frame): the quantity in each stepper is drawn
 * `font:600 15px/1 Manrope`, and its content is a Bangla numeral (`২`, `১`, `৩`).
 * Manrope carries no Bangla digits, so the glyph falls back silently to a platform font
 * at the wrong metrics. Set here in Anek Bangla at the drawn 15px, with the docs/02
 * small-band leading of 1.75; the cell is a fixed 28px-wide box inside a 32px-tall
 * flex row, so the glyph does not move. Every instance is listed in the WP-D report.
 */
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@oprappo/ui';
import type { CartResponse } from '@oprappo/contracts';
import { MAX_CART_QUANTITY, MIN_CART_QUANTITY } from '@oprappo/contracts';
import { toBanglaDigits } from '@oprappo/tokens';
import { Frame, SAND_FRAME, ScreenHeader } from '../offering/chrome.js';
import { Loaded, commerce, useResource } from '../offering/commerce.js';
import { createWriteQueue } from './writes.js';

/** `width:11px;height:2px;background:#0E3A45` inside a 32 × 32 cell. */
function Minus(): JSX.Element {
  return (
    <div className="w-[32px] h-[32px] flex items-center justify-center">
      <div className="w-[11px] h-[2px] bg-teal" />
    </div>
  );
}

/** The same bar plus a `2 × 11` bar absolutely centred over it. */
function Plus(): JSX.Element {
  return (
    <div className="w-[32px] h-[32px] flex items-center justify-center relative">
      <div className="w-[11px] h-[2px] bg-teal" />
      <div className="w-[2px] h-[11px] bg-teal absolute" />
    </div>
  );
}

export function D3CartView({
  cart,
  onStep,
  onAdd,
}: {
  cart: CartResponse;
  onStep?: (lineId: string, quantity: number) => void;
  onAdd?: (offeringId: string) => void;
}): JSX.Element {
  const navigate = useNavigate();

  return (
    <Frame className={SAND_FRAME}>
      <ScreenHeader titleBn="কার্ট" onBack={() => navigate(-1)} />

      <div className="flex-1 px-[16px] oprappo-scroll">
        {cart.lines.map((line) => (
          <div
            key={line.id}
            className="py-[14px] border-b border-border flex gap-[12px] items-center"
          >
            {/* `width:52px;height:52px;border:1px dashed` on a content-box canvas — 54 × 54. */}
            <div className="w-[52px] h-[52px] box-content bg-sand-deep border border-dashed border-border-warm flex-none" />
            <div className="flex-1">
              <div className="font-anek font-medium text-[15px] leading-[1.75] text-ink">
                {line.name_bn}
              </div>
              <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[1px]">
                {line.unit_bn}
              </div>
            </div>
            <div className="border border-border bg-white flex items-center flex-none">
              {/*
               * The stepper's two controls are icon-only, and neither is in docs/02's
               * table of nine fixed accessible names. docs/01 § accessible names: "Do
               * not invent aria-label text; use the specified string." There is no
               * string to use, so none is set and the gap is reported. Two Bangla names
               * are needed before this ships.
               */}
              <button
                type="button"
                onClick={() => onStep?.(line.id, line.quantity - 1)}
                className="bg-transparent border-0 p-0 rounded-none"
              >
                <Minus />
              </button>
              {/* Bangla numeral — Anek Bangla, not the drawn Manrope. See the file header. */}
              <div className="w-[28px] text-center font-anek font-semibold text-[15px] leading-[1.75] text-ink">
                {toBanglaDigits(line.quantity)}
              </div>
              <button
                type="button"
                onClick={() => onStep?.(line.id, line.quantity + 1)}
                className="bg-transparent border-0 p-0 rounded-none"
              >
                <Plus />
              </button>
            </div>
          </div>
        ))}

        {/*
         * The heading is drawn ABOVE the tiles, so with no tiles it is a title over
         * nothing. The server sends no suggestion only when there is nothing left to
         * suggest; rendering the word for a list that is not there is the client
         * asserting something the server did not. Both go or both stay.
         */}
        {cart.suggestions.length === 0 ? null : (
          <div className="mt-[20px]">
            <div className="font-anek font-semibold text-[15px] leading-[1.75] text-muted mb-[10px]">
              সঙ্গে লাগতে পারে
            </div>
            <div className="flex gap-[10px]">
              {cart.suggestions.map((s) => (
                /*
                 * A real control, not a tile with a tap handler: the whole point of this
                 * row is that one tap adds the thing. Every button default that would
                 * move a pixel is reset — `appearance`, the UA border, background,
                 * padding, radius and the centred text — so the drawn tile is unchanged
                 * and the gate still measures what it measured. It needs no `aria-label`:
                 * its accessible name comes from the name and price inside it, which is
                 * why no Bangla is invented here.
                 */
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onAdd?.(s.id)}
                  className="appearance-none flex-1 block text-left bg-white border border-border p-[11px] rounded-none m-0"
                >
                  <div className="font-anek font-medium text-[14px] leading-[1.75] text-ink">
                    {s.name_bn}
                  </div>
                  <div className="font-manrope font-semibold text-[14px] leading-[1] text-ink tabular-nums mt-[6px]">
                    {s.price.display}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border-t border-border shadow-bottombar px-[16px] py-[13px]">
        <div className="flex justify-between items-baseline mb-[11px]">
          {/* পণ্যের দাম, never মোট — and the server is what says so. */}
          <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-body">
            {cart.summary.label_bn}
          </div>
          <div className="font-manrope font-bold text-[20px] leading-[1] text-ink tabular-nums">
            {cart.summary.amount.display}
          </div>
        </div>
        <Button onClick={() => navigate('/checkout')}>চেকআউট</Button>
      </div>
    </Frame>
  );
}

/**
 * What one stepper tap means as a request — or `null`, when it means no request at all.
 *
 * Both ends are the route's own contract, not a preference of this screen's:
 *
 *   below MIN   a REMOVAL. `MIN_CART_QUANTITY` is 1 and the schema refuses 0, because a
 *               line that exists with nothing in it is a line nobody can see and
 *               everybody has to handle. So zero is `DELETE`, not `PATCH 0`.
 *   above MAX   NOTHING. `MAX_CART_QUANTITY` is 99 and the schema refuses 100, so a `+`
 *               tap at the ceiling would spend a round trip to be told what the contract
 *               already says. The client does not send a request it knows the server is
 *               required to refuse.
 *
 * A separate function from the component because this is the part with the edges, and the
 * app's test environment renders to static markup — there is no DOM here to tap.
 */
export function stepWrite(
  lineId: string,
  quantity: number,
): (() => Promise<CartResponse>) | null {
  if (quantity > MAX_CART_QUANTITY) return null;
  if (quantity < MIN_CART_QUANTITY) return () => commerce.removeItem(lineId);
  return () => commerce.setQuantity(lineId, quantity);
}

export function D3Cart(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => commerce.cart(signal), []);
  const [resource, setCart] = useResource(load, 'cart');

  // One queue for the life of the screen. See `writes.ts` for what parallel writes cost.
  const queue = useRef<ReturnType<typeof createWriteQueue>>();
  queue.current ??= createWriteQueue();
  const enqueue = queue.current;

  /**
   * A step is a write, and the cart that comes back is what the screen then shows. Which
   * write — and whether there is one at all — is `stepWrite` above. The failure arm is in
   * the queue: a tap that did not land leaves the rest of the cart alone.
   */
  const step = (lineId: string, quantity: number): void => {
    const write = stepWrite(lineId, quantity);
    if (write === null) return;
    enqueue(() => write().then(setCart));
  };

  /** `সঙ্গে লাগতে পারে` — one tap, one line, at the smallest quantity a line may have. */
  const add = (offeringId: string): void => {
    enqueue(() => commerce.addItem(offeringId, MIN_CART_QUANTITY).then(setCart));
  };

  return (
    <Loaded resource={resource}>
      {(cart) => <D3CartView cart={cart} onStep={step} onAdd={add} />}
    </Loaded>
  );
}
